import { isbot } from 'isbot'
import MouseBotDetector, { DetectionResult } from './mouseBotDetector'
import { load } from '@fingerprintjs/botd'

let sendingResultsStopped = false

enum UserType {
  BOT = 0, // A bot. It may be a good bot that cooperatively identifies itself, or a bad bot that is detected by fingerprinting.
  VISITOR = 1, // A visitor to the site that may or may not be a bot.
  HUMAN = 2 // A visitor that has been classified to be a human by the - painfully simple - heuristic.
}

interface BotDetectionResult extends DetectionResult {
  type: UserType
}

/** Checks if the user agent is cooperatively identifying itself as a bot. */
const currentUserIsKnownGoodBot = () => {
  const userAgent = navigator.userAgent
  return isbot(userAgent)
}

/** Checks if the client can be identified as a bad bot by fingerprinting. */
const currentUserIsKnownBadBot = async () => {
  return load({ monitoring: false })
    .then((botd) => botd.detect())
    .then((result) => {
      return result.bot
    })
}

const sendBotDetectionResultToBackend = (result: BotDetectionResult) => {
  if (sendingResultsStopped) {
    return // This is just a precaution to ensure we don't send multiple results for any reason.
  }

  const oscTestMatch = document.cookie.match(/osc-test\d+-(a|b)/)
  const oscTestGroup = oscTestMatch ? oscTestMatch[1] : null

  if (!oscTestGroup) {
    // eslint-disable-next-line no-console
    console.warn(
      'No osc-test cookie found. Bot detection result will not be sent.'
    )
    return
  }

  const obfuscatedPayload = result.type + '_' + oscTestGroup

  fetch('https://functions.lusini.com/.netlify/functions/bot-detection', {
    method: 'POST',
    body: JSON.stringify(obfuscatedPayload)
  })
}

export const initializeBotDetection = async () => {
  // Run bot detection only in the browser environment.
  if (typeof window === 'undefined') {
    return
  }

  // Every user will initially be reported as a bot which is trying to simulate human behavior.
  // The result will be updated and refined later, if we learn more.
  sendBotDetectionResultToBackend({
    isBot: true,
    type: UserType.VISITOR,
    reasons: ['Default initial bot accusation.']
  })

  if (currentUserIsKnownGoodBot()) {
    // This is a known bot. We don't need to execute any further detection logic.
    sendBotDetectionResultToBackend({
      isBot: true,
      type: UserType.BOT,
      reasons: ['Known bot detected via user agent']
    })
  } else if (await currentUserIsKnownBadBot()) {
    sendBotDetectionResultToBackend({
      isBot: true,
      type: UserType.BOT,
      reasons: ['Known bot detected via fingerprint']
    })
  } else {
    // Exclude touch devices from mouse based bot detection.
    const supportsTouch = navigator.maxTouchPoints > 0
    if (supportsTouch) {
      // Touch devices are not supported by the mouse bot detector.
      // To not classify every touch device as a bot, we assume this is a human.
      // TODO: We may need a strategy to detect bots on touch devices in the future.
      sendBotDetectionResultToBackend({
        isBot: false,
        type: UserType.HUMAN,
        reasons: ['Touch device detected. Assuming human.']
      })
      return
    }

    const detector = new MouseBotDetector()

    detector.addResultListener((result) => {
      if (!result.isBot) {
        // If the user proves that they are not a bot, we update the result.
        sendBotDetectionResultToBackend({
          ...result,
          type: UserType.HUMAN
        })
      }

      sendingResultsStopped = true // Stop sending results to the backend after the first result.

      // We only need one result to send back to the backend. Further monitoring is not necessary.
      detector.stopMonitoring()
    })

    detector.startMonitoring()
  }
}
